import { Col, Row, Typography, Card } from 'antd';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FONT_WEIGHTS, COLORS, TEXT_SIZES } from '../../../utils/constants';
import {
  deleteFacilitySportRequest,
  getSportsByFacilityIdRequest,
} from '../../../api/requests/sports';
import { CourtDto, FacilitySportDto } from '../../../types/types';
import SportOverviewCard from './FacilitySportCard';
import FacilitySportModal from './FacilitySportModal';
import SlotManagement from '../Slots/SlotOverview';
import CourtCard from '../Courts/CourtCard';
import Button from '../../common/Button';
import { PlusCircleOutlined } from '@ant-design/icons';
import CreateCourtModal from '../Courts/CreateCourtModal';
import DeleteModal from '../../shared/DeleteModal';
import UpdateCourtModal from '../Courts/UpdateCourtModal';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import Spinner from '../../common/Spinner';

interface SportsSectionProps {
  facilityId: number;
  facilityName: string;
  onLoadingChange: (isLoading: boolean) => void;
}

const SportsSection: React.FC<SportsSectionProps> = ({
  facilityId,
  facilityName,
  onLoadingChange,
}) => {
  const snackbar = useSnackbar();
  const [isSportsDataLoading, setIsSportsDataLoading] =
    useState<boolean>(false);
  const [sportsOverview, setSportsOverview] = useState<FacilitySportDto[]>([]);
  const [selectedSport, setSelectedSport] = useState<FacilitySportDto | null>(
    null,
  );
  const [editingSport, setEditingSport] = useState<FacilitySportDto | null>(
    null,
  );
  const [isDeletingSport, setIsDeletingSport] = useState<boolean>(false);
  const [isSportModalVisible, setIsSportModalVisible] =
    useState<boolean>(false);
  const [selectedCourt, setSelectedCourt] = useState<CourtDto | null>(null);
  const [isCreatingCourt, setIsCreatingCourt] = useState<boolean>(false);
  const [isEditingCourt, setIsEditingCourt] = useState<boolean>(false);
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);

  const fetchSportsData = async (id: number, showLoading = true) => {
    try {
      if (showLoading) setIsSportsDataLoading(true);
      const response = await getSportsByFacilityIdRequest(id);
      if (response.isSuccess && response.value) {
        setSportsOverview(response.value);
      } else {
        snackbar.error(response.error || 'Failed to fetch sports data');
      }
    } catch (error: any) {
      snackbar.error(
        error?.Error || 'An error occurred while fetching sports data',
      );
    } finally {
      if (showLoading) setTimeout(() => setIsSportsDataLoading(false), 100);
    }
  };

  const onSportClick = (sport: FacilitySportDto) => {
    setSelectedSport(sport);
  };

  const onSportSubmit = () => {
    setIsSportModalVisible(false);
    setEditingSport(null);
    fetchSportsData(facilityId);
  };

  const onDeleteSport = async (facilitySport: FacilitySportDto) => {
    setSelectedSport(facilitySport);
    setIsDeletingSport(true);
  };

  const handleDeleteSport = async () => {
    try {
      setIsSportsDataLoading(true);
      if (selectedSport) {
        const response = await deleteFacilitySportRequest({
          facilityId,
          sportId: selectedSport.sportId,
        });
        if (response.isSuccess) {
          await fetchSportsData(facilityId);
          snackbar.success('Facility Sport deleted successfully');
        } else {
          snackbar.error(
            response.error || 'Error deleting sport of the facility',
          );
        }
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Error deleting sport of the facility');
    } finally {
      setIsSportsDataLoading(false);
      setIsDeletingSport(false);
    }
  };

  const onCourtCreation = () => {
    setIsCreatingCourt(false);
    fetchSportsData(facilityId);
  };

  const onEditCourt = async (court: CourtDto) => {
    setSelectedCourt(court);
    setIsEditingCourt(true);
  };

  const handleCancelEditCourt = () => {
    setSelectedCourt(null);
    setIsEditingCourt(false);
  };

  const handleEditCourt = () => {
    setSelectedCourt(null);
    setIsEditingCourt(false);
    fetchSportsData(facilityId);
  };

  const handleCourtDeletion = () => {
    setSelectedCourt(null);
    fetchSportsData(facilityId);
  };

  const onEditSlot = (court: CourtDto) => {
    setSelectedCourt(court);
    setDrawerVisible(true);
  };

  useEffect(() => {
    fetchSportsData(facilityId);
  }, [facilityId]);

  useEffect(() => {
    onLoadingChange(isSportsDataLoading);
  }, [isSportsDataLoading, onLoadingChange]);

  useEffect(() => {
    if (sportsOverview.length > 0) {
      selectedSport === null && setSelectedSport(sportsOverview[0]);
      const valueToUpdate = sportsOverview.find(
        (item) => item.sportId === selectedSport?.sportId,
      );
      selectedSport?.sportId &&
        valueToUpdate &&
        setSelectedSport(valueToUpdate);
    }
  }, [sportsOverview]);

  if (isSportsDataLoading) {
    return (
      <SpinnerWrapper>
        <Spinner />
      </SpinnerWrapper>
    );
  }

  const AddNewSport = () => (
    <Button
      type="primary"
      width="186px"
      icon={<PlusCircleOutlined style={{ fontSize: TEXT_SIZES.LG }} />}
      onClick={() => setIsSportModalVisible(true)}
      disabled={sportsOverview.length === 7}
    >
      Add New Sport
    </Button>
  );

  return (
    <Col span={24}>
      {sportsOverview.length === 0 && (
        <EmptyCard>
          <Row justify={'space-between'} align={'top'}>
            <Typography.Text strong>Sports & Courts Overview</Typography.Text>
            <AddNewSport />
          </Row>
          <EmptyText>No Sports To Show</EmptyText>
        </EmptyCard>
      )}
      {sportsOverview.length > 0 && (
        <>
          <StyledGrid>
            <Row justify={'space-between'} align={'middle'}>
              <SportsTitle>Sports</SportsTitle>
              <AddNewSport />
            </Row>
            <StyledRow>
              {sportsOverview.map((sport, index) => (
                <StyledCol key={sport.facilitySportId}>
                  <SportOverviewCard
                    isSelected={
                      (selectedSport &&
                        selectedSport.sportId === sport.sportId) ||
                      false
                    }
                    facilitySport={sport}
                    onDeleteSport={onDeleteSport}
                    onEditSport={() => {
                      setEditingSport(sport);
                      setSelectedSport(sport);
                      setIsSportModalVisible(true);
                    }}
                    onSportClick={() => onSportClick(sport)}
                  />
                </StyledCol>
              ))}
            </StyledRow>
          </StyledGrid>
          <CourtsGrid>
            <Row justify={'space-between'} align={'middle'}>
              <SportsTitle>Courts</SportsTitle>
              <Button
                type="primary"
                width="186px"
                icon={
                  <PlusCircleOutlined style={{ fontSize: TEXT_SIZES.LG }} />
                }
                onClick={() => setIsCreatingCourt(true)}
              >
                Add New Court
              </Button>
            </Row>
            <StyledRow>
              {selectedSport?.courts.map((court, index) => (
                <StyledCol key={court.courtId}>
                  <CourtCard
                    court={court}
                    onEditCourt={onEditCourt}
                    onCourtDeletion={handleCourtDeletion}
                    onEditSlot={onEditSlot}
                  />
                </StyledCol>
              ))}
            </StyledRow>
          </CourtsGrid>
        </>
      )}
      {isSportModalVisible && (
        <FacilitySportModal
          visible={isSportModalVisible}
          facilityId={facilityId}
          onSubmit={onSportSubmit}
          onClose={() => {
            setIsSportModalVisible(false);
            setEditingSport(null);
          }}
          initialValues={editingSport}
          sportsOverview={sportsOverview}
        />
      )}
      {isDeletingSport && selectedSport && (
        <DeleteModal
          open={isDeletingSport}
          key={selectedSport.facilitySportId}
          onConfirm={handleDeleteSport}
          onCancel={() => setIsDeletingSport(false)}
          moduleType="Sport"
        />
      )}
      {drawerVisible && selectedSport && selectedCourt && (
        <SlotManagement
          isVisible={drawerVisible}
          selectedSport={selectedSport}
          facilityName={facilityName}
          courtId={selectedCourt?.courtId}
          courtName={selectedCourt.courtName}
          onClose={() => setDrawerVisible(false)}
          onSlotCreated={() => fetchSportsData(facilityId, false)}
        />
      )}
      {isCreatingCourt && selectedSport && (
        <CreateCourtModal
          visible={isCreatingCourt}
          facilitySportId={selectedSport.facilitySportId}
          sportName={selectedSport.sportName}
          onSubmit={onCourtCreation}
          onClose={() => setIsCreatingCourt(false)}
        />
      )}
      {isEditingCourt && selectedCourt && (
        <UpdateCourtModal
          visible={isEditingCourt}
          initialValues={selectedCourt}
          onSubmit={handleEditCourt}
          onClose={handleCancelEditCourt}
        />
      )}
    </Col>
  );
};

const SpinnerWrapper = styled.div`
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyCard = styled(Card)`
  width: 100%;
  height: 200px;
  border: 1px solid ${COLORS.grey.secondaryLight};
  border-radius: 16px;
  .ant-card-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
`;

const EmptyText = styled.div`
  font-size: ${TEXT_SIZES.EIGHT_XL}px;
  font-weight: ${FONT_WEIGHTS.BOLD};
  color: ${COLORS.grey.secondary};
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const StyledGrid = styled.div`
  max-height: 40vh;
  overflow-y: auto;
  padding-right: 4px;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const StyledRow = styled(Row)`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin: 10px 0 0 0;
`;

const StyledCol = styled(Col)`
  width: calc(33.33% - 18px); // 33.33% for 3 cards minus gap
  min-width: 250px;
  flex: 0 0 auto;

  @media (max-width: 1400px) {
    width: calc(33.33% - 16px); // 3 cards per row
  }

  @media (max-width: 1100px) {
    width: calc(50% - 12px); // 2 cards per row
  }

  @media (max-width: 768px) {
    width: 100%; // 1 card per row
  }
`;

const SportsTitle = styled(Typography.Text)`
  font-size: ${TEXT_SIZES.MD}px;
  font-weight: ${FONT_WEIGHTS.BOLD};
`;

const CourtsGrid = styled.div`
  max-height: 30vh;
  overflow-y: auto;
  padding-right: 4px;
  margin-top: 36px;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

export default SportsSection;
